import { Skeleton } from 'antd'
import { ReactComponent as RightArrow } from 'assets/icons/blockyRightArrow16.svg'
import cx from 'classnames'
import Avatar from 'common/Avatar/Avatar'
import { Button } from 'components/Button/Button'
import { ButtonLink } from 'components/Button/ButtonLink'
import { useRequestReadAccess } from 'hooks/queries/articles/useRequestReadAccess'
import { useRef } from 'react'
import Styles from '../CardViewComponent.module.scss'
import { CardViewProps, SkeletonCardProps } from '../cardViewTypes'
import { getDetailsFromBackground } from '../utils/getDetailsFromBackground'
import { buttonLinkText } from '../utils/textUtils'
import { InteractionButtons } from './components/InteractionButtons'

export const ExpandedLargeAvatarCardView = (props: CardViewProps) => {
  const {
    disabled,
    selected,
    commentSelected,
    tasks,
    comments,
    mentionSubject,
    mentionType,
    subtitle,
    url,
    cardStyle,
    articleId,
    loading,
    isError,
  } = props
  const cardRef = useRef<HTMLDivElement>(null)
  const { font, background } = cardStyle
  const details = getDetailsFromBackground(background)
  const buttonStyle = details.darkBackground ? 'ghostWhite' : 'tertiary'
  const [requestReadAccess, requestingAccess] = useRequestReadAccess()
  const backgroundColor = isError ? 'linear-gradient(90deg, #435C6F 7.5%, #889AA8 100%)' : background

  if (loading) return <ExpandedLargeAvatarCardSkeleton selected={selected} />

  const viewButton = () => {
    if (isError && articleId)
      return (
        <Button
          className={Styles.requestAccessButton}
          isDisabled={requestingAccess}
          variant="tertiary"
          endIcon={RightArrow}
          onPress={() => requestReadAccess(articleId)}
        >
          Request Access
        </Button>
      )
    return (
      <ButtonLink
        className={Styles.openLink}
        disabled={disabled}
        to={url}
        variant={buttonStyle}
        endIcon={RightArrow}
        target="_blank"
      >
        {buttonLinkText(mentionType)}
      </ButtonLink>
    )
  }

  return (
    <div
      data-test-id="cardViewComponent"
      ref={cardRef}
      style={{ background: backgroundColor }}
      className={`${cx(Styles.cardView, Styles.expandedLargeAvatar, {
        [Styles.selected]: selected,
        [Styles.commentSelected]: commentSelected,
        [Styles.restricted]: isError,
        [Styles.whiteText]: details.darkBackground || isError,
        [Styles.boxShadow]: details.includeBoxShadow && !isError,
        [Styles.smallSize]: cardRef.current && cardRef.current.offsetWidth < 400,
      })}`}
    >
      <div className={Styles.cardViewRight}>
        <Avatar className={cx(Styles.avatar, Styles.largeAvatar)} size={94} {...mentionSubject.avatar} />
        <div className={Styles.titleContainer}>
          <p className={Styles.title} style={{ fontFamily: font }}>
            {mentionSubject.title}
          </p>
          <p className={Styles.subtitle} style={{ fontFamily: font }}>
            {subtitle}
          </p>
          {viewButton()}
        </div>
      </div>
      <InteractionButtons disabled={disabled} tasks={tasks} comments={comments} />
    </div>
  )
}

const ExpandedLargeAvatarCardSkeleton = ({ selected }: SkeletonCardProps) => {
  return (
    <div
      data-test-id="cardViewComponent"
      className={`${cx(Styles.cardView, Styles.expandedLargeAvatar, Styles.loading, {
        [Styles.selected]: selected,
      })}`}
    >
      <div className={Styles.cardViewRight}>
        <Avatar className={cx(Styles.avatar, Styles.largeAvatar)} size={94} />
        <div className={Styles.titleContainer}>
          <Skeleton className={Styles.title} paragraph={false} title={false} />
          <Skeleton className={Styles.subtitle} paragraph={false} title={false} />
          <Skeleton className={Styles.buttonSkeleton} paragraph={false} title={false} />
        </div>
      </div>
    </div>
  )
}
